import moment from 'moment'

import { convertArrayToHashMap } from '../../../../shared/libraries/dataSets'
import { splitOverlaysIntoCitiesWithPostcodes } from '../../../../Components/CampaignQuote/util'
import { sendEmail } from '../../../Country/Page/Pipeline/PipelineView/graphQL'
import { getXeroRecipient } from '../../../Clients/utils'
import { CAMPAIGN_LOG_ENUM_TYPES } from '../../../../shared/constants'
import { replaceNullValue } from '../../../../Logs/campaign/utils'
import { convertMonetaryDollarStringToCentValue } from '../../../../Utils/Formatters'
import { PATHS } from '../../../../Config/Routing/paths'
import { NAV } from '../../../../Config/Routing/nav'

/**
 *
 * @param {object} args
 * @param {string} args.countryCode
 * @param {object[]} args.overlays
 * @param {number} args.overlays[].latitude
 * @param {number} args.overlays[].longitude
 * @param {object[]} args.cities
 */
export const generateCampaignSummary = ({ countryCode, overlays, cities }) => {
  const citiesMap = convertArrayToHashMap(cities, 'id')
  return splitOverlaysIntoCitiesWithPostcodes({
    overlays,
    citiesMap,
    countryCode,
  })
}

export const formatDateToEndOfDay = date => {
  const withHours = new Date(date).setHours(23)
  const withMinutes = new Date(withHours).setMinutes(59)
  return moment(withMinutes).format()
}

export const formatCityMetrics = cityMetrics =>
  cityMetrics.reduce((acc, item) => {
    if (item.cityId) {
      acc.push({
        'id': item.id,
        'campaignId': item.campaignId,
        'cityId': item.cityId,
        'saverId': item.saverId,
        'totalFlyers': item.totalFlyers,
        'totalMissions': item.totalMissions,
        'distributionCost': item.distributionCost,
        'travelCost': item.travelCost,
        'permitCost': item.permitCost,
        'printingCost': item.printingCost,
        'bonusBudget': item.bonusBudget,
        'postingCost': item.postingCost,
        'dataCost': item.dataCost,
        'outfitCost': item.outfitCost,
        'freightCost': item.freightCost,
      })
    }
    return acc
  }, [])

export const checkIsCityMetricsDifferent = ({
  newCityMetrics,
  oldCityMetrics,
}) => {
  const formattedNewCitiesMetrics = formatCityMetrics(newCityMetrics)
  const formattedOldCitiesMetrics = formatCityMetrics(oldCityMetrics)

  if (!formattedOldCitiesMetrics.length) {
    return {
      ifCityMetricsDifferent: false,
      oldCM: formattedOldCitiesMetrics,
      newCM: formattedNewCitiesMetrics,
    }
  }

  return {
    ifCityMetricsDifferent:
      JSON.stringify(formattedNewCitiesMetrics) !==
      JSON.stringify(formattedOldCitiesMetrics),
    oldCM: formattedOldCitiesMetrics,
    newCM: formattedNewCitiesMetrics,
  }
}

export const formatCityMetricToEmail = (metric, citiesHashMap, isDM) =>
  metric.reduce((acc, item) => {
    if (isDM) {
      acc.push(
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `City - ${citiesHashMap[item.cityId]}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Total flyers - ${item.totalFlyers || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Printing cost - ${item.printingCost || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Posting budget - ${item.postingCost || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Data cost - ${item.dataCost || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Freight cost - ${item.freightCost || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': '------------------------' }],
        },
      )
    } else {
      acc.push(
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `City - ${citiesHashMap[item.cityId]}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Total flyers - ${item.totalFlyers || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [
            { 'text': `Total missions - ${item.totalMissions || 0}` },
          ],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [
            { 'text': `Distribution cost - ${item.distributionCost || 0}` },
          ],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Travel cost - ${item.travelCost || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Permit cost - ${item.permitCost || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Printing cost - ${item.printingCost || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Bonus budget - ${item.bonusBudget || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': `Outfit cost - ${item.outfitCost || 0}` }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': '------------------------' }],
        },
      )
    }

    return acc
  }, [])

export const sendEmailChangeCityMetricToGMsAndCFOC = async ({
  newCityMetric,
  oldCityMetrics,
  country,
  user,
  isDM,
  citiesHashMap,
}) => {
  const countryCode = country.code

  const formattedOldCityMetricToEmail = formatCityMetricToEmail(
    oldCityMetrics,
    citiesHashMap,
    isDM,
  )
  const formattedNewCityMetricToEmail = formatCityMetricToEmail(
    newCityMetric,
    citiesHashMap,
    isDM,
  )

  await sendEmail({
    input: {
      sender: user.email,
      recipients: getXeroRecipient(countryCode),
      cc: [],
      bcc: [],
      subject: 'City Metrics was changed without Xero connection',
      body: JSON.stringify([
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': 'Hello' }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': '' }],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [
            { 'text': 'City Metrics was changed without Xero connection' },
          ],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [
            {
              'text': `User ${user.firstName} ${user.lastName} (${user.role}) ${
                user.email
              }`,
            },
          ],
        },
        {
          'type': 'paragraph',
          'align': 'left',
          'children': [{ 'text': '' }],
        },
        {
          'type': 'bulleted-list',
          'children': [
            {
              'type': 'list-item',
              'align': 'left',
              'children': [{ 'text': 'Old City Metrics', 'bold': true }],
            },
          ],
        },
        ...formattedOldCityMetricToEmail,
        {
          'type': 'bulleted-list',
          'children': [
            {
              'type': 'list-item',
              'align': 'left',
              'children': [{ 'text': 'New City Metric', 'bold': true }],
            },
          ],
        },
        ...formattedNewCityMetricToEmail,
      ]),
    },
  })
}

export const getBonusThresholdModalDescription = bonusThreshold =>
  bonusThreshold ? `${bonusThreshold} %` : 'Disabled'

export const getLogs = ({ initialValues, values, campaignId }) => {
  const logs = []
  if (values.name && initialValues.name !== values.name) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_NAME,
      additionalInfo: `from ${replaceNullValue(
        initialValues.name,
      )} to ${replaceNullValue(values.name)}`,
    })
  }
  if (values.status && initialValues.status !== values.status) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_STATUS,
      additionalInfo: `from ${replaceNullValue(
        initialValues.status,
      )} to ${replaceNullValue(values.status)}`,
    })
  }

  if (values.startDate) {
    const timestamp = new Date(values.startDate).getTime()
    if (initialValues.startDate !== timestamp) {
      logs.push({
        campaignId,
        type: CAMPAIGN_LOG_ENUM_TYPES.SET_START_DATE,
        additionalInfo: `from ${
          initialValues.startDate
            ? moment(initialValues.startDate).format('YYYY-MM-DD')
            : ' - '
        } to ${
          values.startDate
            ? moment(values.startDate).format('YYYY-MM-DD')
            : ' - '
        }`,
      })
    }
  }

  if (values.offerExpirationDate) {
    const timestamp = new Date(values.offerExpirationDate).getTime()
    if (initialValues.offerExpirationDate !== timestamp) {
      logs.push({
        campaignId,
        type: CAMPAIGN_LOG_ENUM_TYPES.SET_OFFER_EXPIRATION_DATE,
        additionalInfo: `from ${
          initialValues.offerExpirationDate
            ? moment(initialValues.offerExpirationDate).format('YYYY-MM-DD')
            : ' - '
        } to ${
          values.offerExpirationDate
            ? moment(values.offerExpirationDate).format('YYYY-MM-DD')
            : ' - '
        }`,
      })
    }
  }

  if (
    values.printingCostPerFlyer &&
    convertMonetaryDollarStringToCentValue(
      initialValues.printingCostPerFlyer,
    ) !== values.printingCostPerFlyer
  ) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_COST_PER_FLYER,
      additionalInfo: `from ${replaceNullValue(
        initialValues.printingCostPerFlyer,
      )} to ${replaceNullValue(
        values.printingCostPerFlyer ? values.printingCostPerFlyer / 100 : null,
      )}`,
    })
  }

  if (
    values.baRedemptionPeriod &&
    initialValues.baRedemptionPeriod !== values.baRedemptionPeriod
  ) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_REDEMPTION_TIME,
      additionalInfo: `from ${replaceNullValue(
        initialValues.baRedemptionPeriod,
      )} to ${replaceNullValue(values.baRedemptionPeriod)}`,
    })
  }

  if (
    values.campaignDurationWeek &&
    initialValues.campaignDurationWeek !== values.campaignDurationWeek
  ) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_DURATION,
      additionalInfo: `from ${replaceNullValue(
        initialValues.campaignDurationWeek,
      )} to ${replaceNullValue(values.campaignDurationWeek)}`,
    })
  }

  if (
    typeof values.noExpiration === 'boolean' &&
    initialValues.noExpiration !== values.noExpiration
  ) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_EXPIRATION,
      additionalInfo: `from ${replaceNullValue(
        initialValues.noExpiration,
      )} to ${replaceNullValue(values.noExpiration)}`,
    })
  }

  if (values.bonusLimit && initialValues.bonusLimit !== values.bonusLimit) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_BONUS_ALLOWANCE,
      additionalInfo: `from ${replaceNullValue(
        initialValues.bonusLimit || null,
      )} to ${replaceNullValue(values.bonusLimit)}`,
    })
  }

  if (
    values.payoutPerConversion &&
    initialValues.payoutPerConversion !== values.payoutPerConversion
  ) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_BONUS_PER_CONVERSION,
      additionalInfo: `from ${replaceNullValue(
        initialValues.payoutPerConversion || null,
      )} to ${replaceNullValue(values.payoutPerConversion)}`,
    })
  }
  if (initialValues.bonusThreshold != values.bonusThreshold) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_BONUS_THRESHOLD,
      additionalInfo: `from ${replaceNullValue(
        initialValues.bonusThreshold || null,
      )} to ${replaceNullValue(values.bonusThreshold)}`,
    })
  }

  if (
    typeof values.conversionsApiEnabled === 'boolean' &&
    initialValues.conversionsApiEnabled !== values.conversionsApiEnabled
  ) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_CONVERSION_API,
      additionalInfo: `from ${replaceNullValue(
        initialValues.conversionsApiEnabled,
      )} to ${replaceNullValue(values.conversionsApiEnabled)}`,
    })
  }
  if (
    typeof values.scanDataEnabled === 'boolean' &&
    initialValues.scanDataEnabled !== values.scanDataEnabled
  ) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_SCAN_DATA,
      additionalInfo: `from ${replaceNullValue(
        initialValues.scanDataEnabled,
      )} to ${replaceNullValue(values.scanDataEnabled)}`,
    })
  }

  if (
    typeof values.receiveReport === 'boolean' &&
    initialValues.campaign.receiveReport !== values.receiveReport
  ) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_RECEIVE_REPORT,
      additionalInfo: `from ${replaceNullValue(
        initialValues.campaign.receiveReport,
      )} to ${replaceNullValue(values.receiveReport)}`,
    })
  }

  if (values.goal && initialValues.campaign.goal !== values.goal) {
    logs.push({
      campaignId,
      type: CAMPAIGN_LOG_ENUM_TYPES.SET_GOAL,
      additionalInfo: `from ${replaceNullValue(
        initialValues.campaign.goal,
      )} to ${replaceNullValue(values.goal)}`,
    })
  }

  return logs
}

export const navigateToArtworks = campaignId => {
  const url =
    PATHS.main.dashboard +
    PATHS.dashboard.campaigns +
    '/' +
    campaignId +
    '/' +
    NAV.CAMPAIGN_HEADER_NAV.DASHBOARD +
    PATHS.campaign.artworkAndOffers
  return window.open(url, '_blank')
}
